import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import Webcam from 'react-webcam';
import Scanner from '../../utils/scanner';
import { useOpenCvLoader } from '../../hooks/useOpenCvLoader';

interface Props {
    open: boolean;
    onClose: () => void;
    onCapture: (file: File) => void;
}

type Phase = 'loading' | 'scanning' | 'captured';

const SCAN_FPS_INTERVAL = 1000 / 15; // ~15fps

const DocumentScanDialog = (props: Props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { status: cvStatus, load: loadCv, retry: retryCv } = useOpenCvLoader();

    const [phase, setPhase] = useState<Phase>('loading');
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [capturedFile, setCapturedFile] = useState<File | null>(null);
    const [noDocumentMsg, setNoDocumentMsg] = useState(false);

    const webcamRef = useRef<Webcam>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const scannerRef = useRef<Scanner | null>(null);
    const animFrameRef = useRef<number>(0);
    const lastFrameTimeRef = useRef<number>(0);

    // Load OpenCV when dialog opens
    useEffect(() => {
        if (props.open) {
            setPhase('loading');
            setCameraError(null);
            setCapturedImage(null);
            setCapturedFile(null);
            setNoDocumentMsg(false);
            loadCv();
        } else {
            // Cleanup on close
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = 0;
            }
        }
    }, [props.open, loadCv]);

    // Transition to scanning once OpenCV is loaded
    useEffect(() => {
        if (cvStatus === 'loaded' && phase === 'loading') {
            scannerRef.current = new Scanner();
            setPhase('scanning');
        }
    }, [cvStatus, phase]);

    // Edge detection loop
    const runDetection = useCallback(() => {
        const now = performance.now();
        if (now - lastFrameTimeRef.current < SCAN_FPS_INTERVAL) {
            animFrameRef.current = requestAnimationFrame(runDetection);
            return;
        }
        lastFrameTimeRef.current = now;

        const video = webcamRef.current?.video;
        const overlay = overlayCanvasRef.current;
        const scanner = scannerRef.current;

        if (!video || !overlay || !scanner || video.readyState !== 4) {
            animFrameRef.current = requestAnimationFrame(runDetection);
            return;
        }

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw === 0 || vh === 0) {
            animFrameRef.current = requestAnimationFrame(runDetection);
            return;
        }

        overlay.width = vw;
        overlay.height = vh;

        // Draw video frame to overlay canvas
        const ctx = overlay.getContext('2d');
        if (!ctx) {
            animFrameRef.current = requestAnimationFrame(runDetection);
            return;
        }
        ctx.drawImage(video, 0, 0, vw, vh);

        try {
            scanner.highlightPaper(overlay, { color: '#00ff00', thickness: 3 });
        } catch {
            // silently ignore detection errors on individual frames
        }

        animFrameRef.current = requestAnimationFrame(runDetection);
    }, []);

    // Start/stop detection loop based on phase
    useEffect(() => {
        if (phase === 'scanning' && props.open) {
            animFrameRef.current = requestAnimationFrame(runDetection);
        }
        return () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = 0;
            }
        };
    }, [phase, props.open, runDetection]);

    const handleCapture = useCallback(() => {
        const video = webcamRef.current?.video;
        const canvas = canvasRef.current;
        const scanner = scannerRef.current;

        if (!video || !canvas || !scanner) return;

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        canvas.width = vw;
        canvas.height = vh;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, vw, vh);

        try {
            const resultCanvas = scanner.extractPaper(canvas, 595, 842);
            if (!resultCanvas) {
                setNoDocumentMsg(true);
                return;
            }
            const dataUrl = resultCanvas.toDataURL('image/jpeg', 0.92);
            setCapturedImage(dataUrl);

            resultCanvas.toBlob(
                (blob) => {
                    if (blob) {
                        const file = new File(
                            [blob],
                            `scan_${Date.now()}.jpg`,
                            { type: 'image/jpeg' }
                        );
                        setCapturedFile(file);
                    }
                },
                'image/jpeg',
                0.92
            );

            // Stop detection loop
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = 0;
            }
            setPhase('captured');
            setNoDocumentMsg(false);
        } catch {
            setNoDocumentMsg(true);
        }
    }, []);

    const handleRecapture = () => {
        setCapturedImage(null);
        setCapturedFile(null);
        setNoDocumentMsg(false);
        setPhase('scanning');
    };

    const handleUse = () => {
        if (capturedFile) {
            props.onCapture(capturedFile);
            handleClose();
        }
    };

    const handleClose = () => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = 0;
        }
        setCapturedImage(null);
        setCapturedFile(null);
        setCameraError(null);
        setNoDocumentMsg(false);
        setPhase('loading');
        props.onClose();
    };

    const toggleCamera = () => {
        setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
    };

    const handleCameraError = useCallback(() => {
        setCameraError(
            'Não foi possível acessar a câmera. Verifique as permissões do navegador e tente novamente.'
        );
    }, []);

    const videoConstraints = {
        facingMode,
        width: { ideal: 1280 },
        height: { ideal: 720 },
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Digitalizar Documento
                {phase === 'scanning' && (
                    <Tooltip title="Trocar câmera">
                        <IconButton onClick={toggleCamera} size="small">
                            <CameraswitchIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative', minHeight: 300 }}>
                {/* Loading phase */}
                {phase === 'loading' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 2 }}>
                        {cvStatus === 'error' ? (
                            <>
                                <Alert severity="error" sx={{ mx: 2 }}>
                                    Falha ao carregar o motor de visão computacional. Verifique sua conexão com a internet.
                                </Alert>
                                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={retryCv}>
                                    Tentar Novamente
                                </Button>
                            </>
                        ) : (
                            <>
                                <CircularProgress size={48} />
                                <Typography color="text.secondary">
                                    Carregando motor de detecção...
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                {/* Scanning phase */}
                {phase === 'scanning' && !cameraError && (
                    <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000' }}>
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            videoConstraints={videoConstraints}
                            onUserMediaError={handleCameraError}
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                            }}
                        />
                        {/* Overlay canvas for edge detection visualization */}
                        <canvas
                            ref={overlayCanvasRef}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                            }}
                        />
                        {/* Hidden canvas for capture processing */}
                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                        {/* Guide text */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body2">
                                Posicione o documento sobre uma superfície escura
                            </Typography>
                        </Box>

                        {noDocumentMsg && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    bgcolor: 'rgba(255,152,0,0.9)',
                                    color: '#fff',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="body2">
                                    Nenhum documento detectado. Reposicione e tente novamente.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Camera error */}
                {phase === 'scanning' && cameraError && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 2, px: 2 }}>
                        <Alert severity="error">{cameraError}</Alert>
                    </Box>
                )}

                {/* Captured phase */}
                {phase === 'captured' && capturedImage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2 }}>
                        <img
                            src={capturedImage}
                            alt="Documento escaneado"
                            style={{
                                maxWidth: '100%',
                                maxHeight: isMobile ? '60vh' : 500,
                                objectFit: 'contain',
                                borderRadius: 4,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                {phase === 'scanning' && !cameraError && (
                    <>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button
                            variant="contained"
                            startIcon={<PhotoCameraIcon />}
                            onClick={handleCapture}
                            size="large"
                        >
                            Capturar
                        </Button>
                    </>
                )}

                {phase === 'scanning' && cameraError && (
                    <Button onClick={handleClose}>Fechar</Button>
                )}

                {phase === 'captured' && (
                    <>
                        <Button startIcon={<ReplayIcon />} onClick={handleRecapture}>
                            Recapturar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<CheckIcon />}
                            onClick={handleUse}
                            disabled={!capturedFile}
                        >
                            Usar
                        </Button>
                    </>
                )}

                {phase === 'loading' && cvStatus === 'error' && (
                    <Button onClick={handleClose}>Fechar</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DocumentScanDialog;
